import React from "react";

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useNavigate, useParams } from "react-router-dom";

import ActionsMenu from "../../../../../components/ActionsMenu";
import { HistoryMenuProps } from "../../../@types/rldynamicanalysis";
import {
  useDownloadDroppedFiles,
  useDownloadMemstrings,
  useDownloadPcaps,
  useDownloadScreenshots,
  useDroppedFilesStaticAnalysis,
  useGetRLSandboxResourcesStatus
} from "../../../hooks";
import { DROPPED_FILES_ACTIONS } from "../consts";
import { getDownloadDroppedFilesDisabledMessage } from "../utils";

const HistoryMenu: React.FC<HistoryMenuProps> = props => {
  const { sourceId } = useParams();
  const {
    hash,
    analysisId,
    isStoredReport,
    isNetworkReport = false,
    relatedAnalysis = false,
    url
  } = props || {};

  const { downloadMemstrings } = useDownloadMemstrings(
    hash,
    analysisId,
    isNetworkReport
  );
  const { downloadScreenshots } = useDownloadScreenshots(
    hash,
    analysisId,
    isNetworkReport
  );
  const { downloadPcaps } = useDownloadPcaps(hash, analysisId, isNetworkReport);
  const { downloadDroppedFiles } = useDownloadDroppedFiles(
    hash,
    analysisId,
    isNetworkReport
  );
  const { sendToStaticAnalysis, isLoading } = useDroppedFilesStaticAnalysis(
    hash,
    analysisId
  );
  const { fetchStatus, data } = useGetRLSandboxResourcesStatus(
    hash,
    analysisId,
    isNetworkReport
  );

  const navigate = useNavigate();

  const sendToStaticAnalysisDisabledMsg =
    getDownloadDroppedFilesDisabledMessage(
      isStoredReport,
      data?.has_dropped_files,
      data?.is_private,
      isLoading,
      sourceId
    );

  const actions = [
    {
      label: "Download Screenshots",
      onClick: () => {
        downloadScreenshots();
      },
      disabled: data?.is_private || !data?.has_screenshots,
      disabledMessage: data?.is_private
        ? "Screenshots are not available for private samples."
        : "Screenshots no longer available. Reprocessing the file generates new artifacts."
    },
    {
      label: "Download PCAPs",
      onClick: () => {
        downloadPcaps();
      },
      disabled: data?.is_private || !data?.has_pcaps,
      disabledMessage: data?.is_private
        ? "PCAPs are not available for private samples."
        : "PCAPs no longer available. Reprocessing the file generates new artifacts."
    },
    {
      label: "Download MemStrings",
      onClick: () => {
        downloadMemstrings();
      },
      disabled: data?.is_private || !data?.has_memstrings,
      disabledMessage: data?.is_private
        ? "MemStrings are not available for private samples."
        : "MemStrings no longer available. Reprocessing the file generates new artifacts."
    },
    {
      label: DROPPED_FILES_ACTIONS.DOWNLOAD,
      onClick: () => {
        downloadDroppedFiles();
      },
      disabled: data?.is_private || !data?.has_dropped_files,
      disabledMessage: data?.is_private
        ? "Dropped files are not available for private samples."
        : "Dropped files no longer available. Reprocessing the file generates new artifacts."
    },
    {
      label: DROPPED_FILES_ACTIONS.SEND_TO_STATIC_ANALYSIS,
      onClick: () => {
        sendToStaticAnalysis();
      },
      disabled:
        data?.is_private ||
        !data?.has_dropped_files ||
        isLoading ||
        !sourceId ||
        !!url,
      disabledMessage: sendToStaticAnalysisDisabledMsg
    }
  ];

  if (relatedAnalysis) {
    actions.unshift({
      label: "See Analysis Details",
      onClick: () => {
        if (url) {
          navigate(
            `/url/${encodeURIComponent(url)}/${hash}/dynamic/signatures/`
          );
        } else {
          navigate(`/redirect/${hash}/rl_dynamic_analysis/`);
        }
      },
      disabled: false,
      disabledMessage: ""
    });
  }

  const loadingAction = [
    {
      label: "Loading",
      disabled: true
    }
  ];

  const actionsMenuProps = {
    id: `actions-${props.analysisId}`,
    actions: data != null ? actions : loadingAction,
    onToggle: (isShown: boolean) => {
      if (isShown && !data) {
        fetchStatus();
      }
    },
    buttonClassName: "hamburger-menu",
    stopPropagationOnClick: false
  };

  if (isStoredReport) {
    return (
      <OverlayTrigger
        key={`rl-cloud-sandbox-history-menu-${props.analysisId}`}
        overlay={
          <Tooltip
            id="menu-action-warning-tooltip"
            className="menu-action-tooltip"
          >
            Actions not available for locally stored reports.
          </Tooltip>
        }
        placement="top"
      >
        <div>
          <ActionsMenu {...actionsMenuProps} disabled />
        </div>
      </OverlayTrigger>
    );
  }

  return <ActionsMenu {...actionsMenuProps} />;
};

export default HistoryMenu;
